import { useCallback, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface Props {
  open: boolean;
  imageSrc: string;
  onClose: () => void;
  onSave: (croppedDataUrl: string) => void;
}

const cropToDataUrl = async (
  imageSrc: string,
  area: Area,
  outputSize = 256,
): Promise<string> => {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');
  ctx.drawImage(
    img,
    area.x, area.y, area.width, area.height,
    0, 0, outputSize, outputSize,
  );
  return canvas.toDataURL('image/png');
};

export const BrandLogoCropper = ({ open, imageSrc, onClose, onSave }: Props) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPx, setAreaPx] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setAreaPx(pixels);
  }, []);

  const handleSave = async () => {
    if (!areaPx) return;
    setBusy(true);
    try {
      const dataUrl = await cropToDataUrl(imageSrc, areaPx);
      onSave(dataUrl);
      // Reset for next open
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Crop logo"
      maxWidth={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={!areaPx || busy}>
            {busy ? 'Cropping…' : 'Use this crop'}
          </Button>
        </>
      }
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: 360,
          background: 'var(--bg-subtle)',
          borderRadius: 'var(--r-md)',
          overflow: 'hidden',
        }}
      >
        {imageSrc && (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="rect"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        )}
      </div>
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12, color: 'var(--ink-3)', minWidth: 40 }}>Zoom</span>
        <input
          type="range"
          min={1}
          max={4}
          step={0.05}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          style={{ flex: 1 }}
        />
      </div>
      <p style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 8 }}>
        Drag to position, scroll or use the slider to zoom. Output is square so it fits the brand avatar.
      </p>
    </Modal>
  );
};

export default BrandLogoCropper;
